import { useEffect, useState } from "react";
import axios from "../AxiosInstance";
import Sidebar from "./Sidebar";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Table } from "antd";
import RecentluActivity from "../pages/admin/RecentluActivity";
import { FaUsers, FaUserPlus, FaUserCheck, FaChartLine } from "react-icons/fa";

const totalUsersData = [
  { name: "Mon", users: 100 },
  { name: "Tue", users: 200 },
  { name: "Wed", users: 300 },
  { name: "Thu", users: 250 },
  { name: "Sun", users: 500 },
];

const newUsersData = [
  { name: "Mon", users: 10 },
  { name: "Tue", users: 20 },
  { name: "Wed", users: 15 },
  { name: "Thu", users: 30 },
  { name: "Sun", users: 25 },
];

const activeUsersData = [
  { name: "Mon", users: 50 },
  { name: "Tue", users: 60 },
  { name: "Wed", users: 70 },
  { name: "Thu", users: 90 },
  { name: "Sun", users: 80 },
];
const Dashboard = () => {
  const [signupCount, setSignupCount] = useState(0);
  const [newUsers, setNewUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [dayWiseUsers, setDayWiseUsers] = useState([]);
  const [dayWiseNewUsers, setDayWiseNewUsers] = useState([]);
  const [dayWiseUsersActive, setDayWiseUsersAcrive] = useState([]);
  const [users, setUsers] = useState([]); // ✅ Default empty array

  // Fetch the signup count from your backend when the component mounts
  useEffect(() => {
    const fetchSignupCount = async () => {
      try {
        const response = await axios.get("/api/v1/auth/signup-count");
        if (response.data.success) {
          setSignupCount(response.data.totalUsers); // Update the signup count
        } else {
          console.error("Error fetching signup count:", response.data.message);
        }
      } catch (err) {
        console.error("Error fetching signup count:", err);
      }
    };
    fetchSignupCount();
  }, []);

  useEffect(() => {
    const fetchNewUsers = async () => {
      try {
        const res = await axios.get("/api/v1/auth/new-users");
        if (res.data.success) {
          setNewUsers(res.data.newUsers);
        } else {
          console.error("Error fetching new users:", res.data.message);
        }
      } catch (err) {
        console.error("New Users Error:", err);
      }
    };
    fetchNewUsers();
  }, []);

  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        const res = await axios.get("/api/v1/auth/active-users");
        if (res.data.success) {
          setActiveUsers(res.data.activeUsers);
        } else {
          console.error("Error fetching active users:", res.data.message);
        }
      } catch (err) {
        console.error("Active Users Error:", err);
      }
    };
    fetchActiveUsers();
  }, []);

  useEffect(() => {
    const fetchSignupByDay = async () => {
      try {
        const res = await axios.get("/api/v1/auth/signup-by-day");
        if (res.data.success) {
          setDayWiseUsers(res.data.data);
        }
      } catch (err) {
        console.error("Signup by Day Error:", err);
      }
    };
    fetchSignupByDay();
  }, []);

  useEffect(() => {
    const fetchNewUsersByDay = async () => {
      try {
        const res = await axios.get("/api/v1/auth/new-users-by-day");
        if (res.data.success) {
          setDayWiseNewUsers(res.data.data);
        }
      } catch (err) {
        console.error("New users by Day Error:", err);
      }
    };
    fetchNewUsersByDay();
  }, []);
  useEffect(() => {
    const fetchActiveByDay = async () => {
      try {
        const res = await axios.get("/api/v1/auth/active-by-day");
        if (res.data.success) {
          setDayWiseUsersAcrive(res.data.data);
        }
      } catch (err) {
        console.error("Active by Day Error:", err);
      }
    };
    fetchActiveByDay();
  }, []);

  const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const sortedDayWiseUsers = [...dayWiseUsers].sort(
    (a, b) => dayOrder.indexOf(a.name) - dayOrder.indexOf(b.name)
  );

  const sortedDayWiseNewUsers = [...dayWiseNewUsers].sort(
    (a, b) => dayOrder.indexOf(a.name) - dayOrder.indexOf(b.name)
  );

  const sortedDayWiseUsersActive = [...dayWiseUsersActive].sort(
    (a, b) => dayOrder.indexOf(a.name) - dayOrder.indexOf(b.name)
  );

  // Chart data configuration
  const totalUsers = signupCount; // Total number of signups
  const remaining = Math.max(100 - totalUsers, 0); // Ensure it doesn't go negative

  useEffect(() => {
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    try {
      const storedAuth = JSON.parse(localStorage.getItem("auth"));
      const token = storedAuth?.token;

      if (!token) {
        console.error("❌ Token missing in localStorage");
        return;
      }

      const res = await axios.get("api/v1/auth/getAllUsers", {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ API Response:", res.data); // Debugging ke liye

      if (res.data && res.data.success) {
        // ✅ Ensure every user has a 'key' for AntD Table
        const usersWithKeys = res.data.data.map((user, index) => ({
          ...user,
          key: user._id || index, // AntD requires a unique key
        }));

        console.log("✅ Users With Keys:", usersWithKeys); // Debugging
        setUsers(usersWithKeys);
      }
    } catch (error) {
      console.error(
        "❌ Error fetching users:",
        error.response?.data?.message || error.message
      );
    }
  };
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Date Joined",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString("en-GB"), // format as dd/mm/yyyy
    },
  ];
  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Total Users Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FaUsers className="text-2xl text-blue-600" />
                </div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Users</span>
              </div>
              <div className="mb-4">
                <h3 className="text-3xl font-bold text-gray-800">{signupCount.toLocaleString()}</h3>
                <p className="text-sm text-gray-600 mt-1">All registered users</p>
              </div>
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sortedDayWiseUsers}>
                    <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: "#6b7280" }}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        fontSize: "12px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* New Users Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <FaUserPlus className="text-2xl text-green-600" />
                </div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">New Users</span>
              </div>
              <div className="mb-4">
                <h3 className="text-3xl font-bold text-gray-800">{newUsers.toLocaleString()}</h3>
                <p className="text-sm text-gray-600 mt-1">Joined this week</p>
              </div>
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sortedDayWiseNewUsers}>
                    <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: "#6b7280" }}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        fontSize: "12px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ fill: "#10b981", r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Active Users Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <FaUserCheck className="text-2xl text-orange-600" />
                </div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Users</span>
              </div>
              <div className="mb-4">
                <h3 className="text-3xl font-bold text-gray-800">{activeUsers.toLocaleString()}</h3>
                <p className="text-sm text-gray-600 mt-1">Active this week</p>
              </div>
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sortedDayWiseUsersActive}>
                    <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: "#6b7280" }}
                      axisLine={{ stroke: "#e5e7eb" }}
                    />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        fontSize: "12px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke="#f97316"
                      strokeWidth={2}
                      dot={{ fill: "#f97316", r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Bottom Section - Users Table and Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Users Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaChartLine className="text-[#34c3c9] text-xl" />
                  <h2 className="text-lg font-semibold text-gray-800">Recent Users</h2>
                </div>
              </div>
              <div className="overflow-x-auto">
                {users.length > 0 ? (
                  <Table
                    columns={columns}
                    dataSource={users}
                    pagination={{ pageSize: 5 }}
                    scroll={{ x: "max-content" }}
                    size="small"
                  />
                ) : (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#34c3c9] mb-2"></div>
                      <p className="text-gray-500">Loading users...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <RecentluActivity />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
